import { FC } from "react";
import {
  Button,
  Group,
  Modal,
  MultiSelect,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createCompetitionGroup } from "../../../../openapi/api-client";
import { v4 as uuid } from "uuid";

interface Team {
  id: string;
  name: string;
}

interface CreateGroupModalProps {
  opened: boolean;
  onClose: () => void;
  competitionId: string;
  enrolledTeams: Team[];
}

export const CreateGroupModal: FC<CreateGroupModalProps> = ({
  opened,
  onClose,
  competitionId,
  enrolledTeams,
}) => {
  const queryClient = useQueryClient();

  const form = useForm<{ name: string; teamIds: string[] }>({
    initialValues: {
      name: "",
      teamIds: [],
    },
    validate: {
      name: (value) => (!value.trim() ? "Name erforderlich" : null),
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: createCompetitionGroup,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["competition", { competitionId }],
      });
      onClose();
      form.reset();
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    await createGroupMutation.mutateAsync({
      id: competitionId,
      groupId: uuid(),
      name: values.name,
      teamIds: values.teamIds,
    });
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Neue Gruppe erstellen">
      <form onSubmit={handleSubmit}>
        <Stack>
          <TextInput
            label="Gruppenname"
            placeholder="z.B. Gruppe A"
            required
            {...form.getInputProps("name")}
          />

          <MultiSelect
            label="Initiale Mannschaften (optional)"
            placeholder="Mannschaften auswählen"
            data={enrolledTeams.map((t) => ({
              label: t.name,
              value: t.id,
            }))}
            searchable
            nothingFoundMessage="Keine passende Mannschaft gefunden"
            {...form.getInputProps("teamIds")}
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" loading={createGroupMutation.isPending}>
              Gruppe erstellen
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
